// src/pages/agent-dashboard/components/ActiveListings.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useConfirm } from '../../../context/ConfirmContext';
import { useToast } from '../../../context/ToastContext';
import { apiRequest } from '../../../services/apiClient';

const PRODUCT_STATUSES = ['ACTIVE', 'INACTIVE', 'SOLD'];

const STATUS_LABELS = {
  ACTIVE: 'Aktif',
  INACTIVE: 'Pasif',
  SOLD: 'Satıldı',
};

const STATUS_CLASSES = {
  ACTIVE: 'bg-success-100 text-success',
  INACTIVE: 'bg-warning-100 text-warning',
  SOLD: 'bg-secondary-100 text-secondary-600',
};

const getSortValue = (item, field) => {
  switch (field) {
    case 'title':
      return `${item?.title || ''}`.toLowerCase();
    case 'category':
      return `${item?.category || ''}`.toLowerCase();
    case 'productType':
      return `${item?.productType || ''}`.toLowerCase();
    case 'quantity':
      return Number(item?.quantity || 0);
    case 'price':
      return Number(item?.price || 0);
    case 'status':
      return `${item?.status || ''}`.toLowerCase();
    case 'createdAt':
      return new Date(item?.createdAt || 0).getTime();
    default:
      return '';
  }
};

const formatPrice = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }

  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('tr-TR');
};

const ActiveListings = ({
  selectedListings = [],
  onSelectionChange,
  onBulkAction,
  onEditProduct,
  refreshKey = 0,
}) => {
  const { confirm } = useConfirm();
  const { success: toastSuccess, error: toastError } = useToast();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pendingStatusById, setPendingStatusById] = useState({});
  const [deletingIds, setDeletingIds] = useState([]);

  const normalizeErrorMessage = (message) => {
    const normalized = `${message || ''}`.trim();
    if (!normalized) {
      return 'Ürünler yüklenemedi.';
    }

    if (normalized.toLowerCase().includes('pagesize must not be greater than 100')) {
      return 'Sayfa boyutu 100 değerinden büyük olamaz.';
    }

    if (normalized === 'Request failed.') {
      return 'İstek başarısız oldu.';
    }

    return normalized;
  };

  const loadProducts = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await apiRequest('/market/products?page=1&pageSize=100', { auth: true });
      const nextProducts = Array.isArray(response) ? response : response?.items || [];
      setProducts(nextProducts);
    } catch (error) {
      const message = normalizeErrorMessage(error?.message);
      setErrorMessage(message);
      toastError(message);
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts, refreshKey]);

  useEffect(() => {
    if (!selectedListings?.length) {
      return;
    }

    const productIdSet = new Set(products.map((product) => product?.id));
    const normalized = selectedListings.filter((id) => productIdSet.has(id));
    if (normalized.length !== selectedListings.length) {
      onSelectionChange?.(normalized);
    }
  }, [onSelectionChange, products, selectedListings]);

  const sortedProducts = useMemo(() => {
    const next = [...products];
    next.sort((a, b) => {
      const aValue = getSortValue(a, sortField);
      const bValue = getSortValue(b, sortField);

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return next;
  }, [products, sortDirection, sortField]);

  const selectedSet = useMemo(() => new Set(selectedListings), [selectedListings]);
  const hasProducts = sortedProducts.length > 0;
  const allSelected =
    hasProducts && sortedProducts.every((product) => selectedSet.has(product?.id));

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onSelectionChange?.(sortedProducts.map((product) => product?.id));
      return;
    }
    onSelectionChange?.([]);
  };

  const handleSelectProduct = (productId, checked) => {
    const next = new Set(selectedListings);
    if (checked) {
      next.add(productId);
    } else {
      next.delete(productId);
    }
    onSelectionChange?.(Array.from(next));
  };

  const updateStatus = async (productId, nextStatus) => {
    if (!PRODUCT_STATUSES.includes(nextStatus)) {
      return;
    }

    setPendingStatusById((prev) => ({ ...prev, [productId]: true }));

    try {
      const updated = await apiRequest(`/market/products/${productId}`, {
        method: 'PATCH',
        auth: true,
        body: { status: nextStatus },
      });

      setProducts((prev) => prev.map((item) => (item.id === productId ? updated : item)));
      toastSuccess(`Ürün durumu "${STATUS_LABELS[nextStatus] || nextStatus}" olarak güncellendi.`);
    } catch (error) {
      toastError(normalizeErrorMessage(error?.message) || 'Durum güncellenemedi.');
    } finally {
      setPendingStatusById((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const handleStatusToggle = (product) => {
    if (!product || (product.status !== 'ACTIVE' && product.status !== 'INACTIVE')) {
      return;
    }

    const nextStatus = product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    updateStatus(product.id, nextStatus);
  };

  const removeProduct = async (product) => {
    const shouldDelete = await confirm({
      title: 'Ürün silinsin mi?',
      message: `"${product?.title || 'Bu ürün'}" kalıcı olarak silinecek.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      tone: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    setDeletingIds((prev) => Array.from(new Set([...prev, product.id])));
    try {
      await apiRequest(`/market/products/${product.id}`, {
        method: 'DELETE',
        auth: true,
      });

      setProducts((prev) => prev.filter((item) => item.id !== product.id));
      onSelectionChange?.((selectedListings || []).filter((id) => id !== product.id));
      toastSuccess('Ürün silindi.');
    } catch (error) {
      toastError(normalizeErrorMessage(error?.message) || 'Ürün silinemedi.');
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== product.id));
    }
  };

  const handleBulkActionChange = async (event) => {
    const action = event.target.value;
    event.target.value = '';

    if (!action || selectedListings.length === 0) {
      return;
    }

    onBulkAction?.(action, selectedListings);

    const targetProducts = products.filter((product) => selectedSet.has(product.id));
    if (!targetProducts.length) {
      return;
    }

    if (action === 'DELETE') {
      const shouldDelete = await confirm({
        title: 'Seçili ürünler silinsin mi?',
        message: `${targetProducts.length} ürün kalıcı olarak silinecek.`,
        confirmText: 'Sil',
        cancelText: 'Vazgeç',
        tone: 'danger',
      });

      if (!shouldDelete) {
        return;
      }

      setDeletingIds((prev) => Array.from(new Set([...prev, ...targetProducts.map((item) => item.id)])));
      try {
        const results = await Promise.allSettled(
          targetProducts.map((product) =>
            apiRequest(`/market/products/${product.id}`, {
              method: 'DELETE',
              auth: true,
            }),
          ),
        );

        const successIds = targetProducts
          .filter((_, index) => results[index]?.status === 'fulfilled')
          .map((product) => product.id);
        const failedCount = results.length - successIds.length;

        if (successIds.length > 0) {
          const successIdSet = new Set(successIds);
          setProducts((prev) => prev.filter((item) => !successIdSet.has(item.id)));
          onSelectionChange?.((selectedListings || []).filter((id) => !successIdSet.has(id)));
          toastSuccess(`${successIds.length} ürün silindi.`);
        }

        if (failedCount > 0) {
          toastError(`${failedCount} ürün silinemedi.`);
        }
      } finally {
        setDeletingIds((prev) =>
          prev.filter((id) => !targetProducts.some((product) => product.id === id)),
        );
      }
      return;
    }

    if (action.startsWith('STATUS:')) {
      const nextStatus = action.replace('STATUS:', '');
      if (!PRODUCT_STATUSES.includes(nextStatus)) {
        return;
      }

      const targetIds = targetProducts.map((item) => item.id);
      setPendingStatusById((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = true;
        });
        return next;
      });

      try {
        const results = await Promise.allSettled(
          targetProducts.map((product) =>
            apiRequest(`/market/products/${product.id}`, {
              method: 'PATCH',
              auth: true,
              body: { status: nextStatus },
            }),
          ),
        );

        const updatedMap = new Map();
        let failedCount = 0;

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value?.id) {
            updatedMap.set(result.value.id, result.value);
          } else {
            failedCount += 1;
          }
        });

        if (updatedMap.size > 0) {
          setProducts((prev) =>
            prev.map((item) => {
              const updated = updatedMap.get(item.id);
              return updated || item;
            }),
          );
          toastSuccess(`${updatedMap.size} ürün güncellendi.`);
        }

        if (failedCount > 0) {
          toastError(`${failedCount} ürün güncellenemedi.`);
        }
      } finally {
        setPendingStatusById((prev) => {
          const next = { ...prev };
          targetIds.forEach((id) => delete next[id]);
          return next;
        });
      }
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-text-primary font-heading">
              Ürünlerim ({products.length})
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              Satıcı akışında oluşturduğun ürünleri buradan yönetebilirsin.
            </p>
          </div>

          {selectedListings.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-secondary">{selectedListings.length} seçildi</span>
              <select
                onChange={handleBulkActionChange}
                className="border border-border rounded-md px-3 py-1 text-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 bg-background"
                defaultValue=""
              >
                <option value="" disabled>
                  Toplu İşlem
                </option>
                <option value="STATUS:ACTIVE">Aktif Yap</option>
                <option value="STATUS:INACTIVE">Pasif Yap</option>
                <option value="STATUS:SOLD">Satıldı Olarak İşaretle</option>
                <option value="DELETE">Seçili Ürünleri Sil</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center">
          <Icon name="Loader2" size={24} className="mx-auto animate-spin text-text-secondary mb-2" />
          <p className="text-text-secondary text-sm">Ürünler yükleniyor...</p>
        </div>
      ) : errorMessage ? (
        <div className="py-16 text-center px-6">
          <Icon name="AlertTriangle" size={28} className="mx-auto text-warning mb-3" />
          <p className="text-text-primary font-medium mb-1">Ürünler yüklenemedi</p>
          <p className="text-text-secondary text-sm mb-4">{errorMessage}</p>
          <button
            type="button"
            onClick={loadProducts}
            className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors duration-200"
          >
            Tekrar Dene
          </button>
        </div>
      ) : !hasProducts ? (
        <div className="text-center py-16 px-6">
          <Icon name="Package" size={48} className="mx-auto text-secondary-300 mb-3" />
          <p className="text-text-primary font-medium">Henüz ürün yok</p>
          <p className="text-text-secondary text-sm mt-1">
            İlk ürünü eklemek için "Yeni Ürün Oluştur" butonunu kullan.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="rounded border-border text-primary focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('title')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Ürün</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('category')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Kategori</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('quantity')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Miktar</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('price')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Fiyat</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Durum</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center space-x-1 hover:text-text-primary transition-colors duration-200"
                  >
                    <span>Oluşturulma</span>
                    <Icon name="ArrowUpDown" size={12} />
                  </button>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedProducts.map((product) => {
                const isUpdating = Boolean(pendingStatusById[product.id]);
                const isDeleting = deletingIds.includes(product.id);
                const statusClass = STATUS_CLASSES[product.status] || STATUS_CLASSES.ACTIVE;
                const isToggleable = product.status === 'ACTIVE' || product.status === 'INACTIVE';

                return (
                  <tr
                    key={product.id}
                    className="hover:bg-secondary-100 transition-colors duration-200"
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedSet.has(product.id)}
                        onChange={(event) => handleSelectProduct(product.id, event.target.checked)}
                        className="rounded border-border text-primary focus:ring-primary-500"
                        disabled={isDeleting}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-text-primary">{product.title || '-'}</p>
                        <p className="text-xs text-text-secondary mt-1">{product.location || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text-primary">
                        <p>{product.category || '-'}</p>
                        <p className="text-xs text-text-secondary mt-1">{product.productType || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-text-primary">
                        <p>
                          Toplam: {Number(product.quantity || 0).toLocaleString('tr-TR')} {product.unit || ''}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          Kalan: {Number(product.availableQuantity ?? product.quantity ?? 0).toLocaleString('tr-TR')} {product.unit || ''}
                        </p>
                        <p className="text-xs text-text-secondary mt-1">
                          Satılan: {Number(product.soldQuantity ?? 0).toLocaleString('tr-TR')} {product.unit || ''}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-primary">{formatPrice(product.price)}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => handleStatusToggle(product)}
                          disabled={isUpdating || isDeleting || !isToggleable}
                          className={`inline-flex w-fit px-2 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                            isToggleable
                              ? 'cursor-pointer hover:opacity-85 hover:shadow-elevation-1 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                              : 'opacity-70 cursor-not-allowed'
                          } ${statusClass}`}
                          title={isToggleable ? 'Durumu değiştir' : 'Bu durum değiştirilemez'}
                        >
                          {isUpdating ? 'Güncelleniyor...' : STATUS_LABELS[product.status] || product.status}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-primary">{formatDate(product.createdAt)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        <Link
                          to={`/property-details?id=${product.id}`}
                          className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                          title="Ürünü Görüntüle"
                        >
                          <Icon name="Eye" size={16} />
                        </Link>
                        <button
                          type="button"
                          onClick={() => onEditProduct?.(product)}
                          className="text-text-secondary hover:text-primary transition-colors duration-200 disabled:opacity-60"
                          title="Ürünü Düzenle"
                          disabled={isDeleting || isUpdating}
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeProduct(product)}
                          className="text-text-secondary hover:text-error transition-colors duration-200 disabled:opacity-60"
                          title="Ürünü Sil"
                          disabled={isDeleting}
                        >
                          {isDeleting ? (
                            <Icon name="Loader2" size={16} className="animate-spin" />
                          ) : (
                            <Icon name="Trash2" size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActiveListings;
